import { HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { CourtSystemErrorCode } from '@ftr/contracts/api/court-system'
import { DepartmentErrorCode } from '@ftr/contracts/api/department'
import { FileErrorCode } from '@ftr/contracts/api/file'
import { LocationErrorCode } from '@ftr/contracts/api/location'
import { MultifactorAuthenticationErrorCode } from '@ftr/contracts/api/mfa'
import { OrderErrorCode, PaymentFailedCardError, PaymentFailedError } from '@ftr/contracts/api/order'
import { OrganizationErrorCode } from '@ftr/contracts/api/organizations'
import { RealTimeSttErrorCode } from '@ftr/contracts/api/real-time-stt'
import { RecorderErrorCode } from '@ftr/contracts/api/recorder'
import { SharedRecordingErrorCode } from '@ftr/contracts/api/shared-recording'
import { SttErrorCode } from '@ftr/contracts/api/stt'
import { TranscriptTurnaroundErrorCode } from '@ftr/contracts/api/transcript-turnaround'
import { UserErrorCode } from '@ftr/contracts/api/user'
import { UserGroupErrorCode } from '@ftr/contracts/api/user-group'
import {
  HybridProducerNotReadyError,
  LiveStreamErrorCode,
  NoHybridCapableProducerFoundForLocationError,
  RealtimeRecorderErrorCode,
} from '@ftr/contracts/regional-api'
import { Money } from '@ftr/contracts/type/shared'
import { StreamLocationErrorCode } from '@ftr/regional-api-contracts'
import { plainToClass } from '@ftr/serialization'
import { StatusCodes } from 'http-status-codes'
import {
  AddonLimitBelowActiveCountError,
  AddonLimitExceedsCourtroomLimitError,
  CannotUpdateClaimError,
  ClientError,
  ConflictApiError,
  CostWaiverDisabled,
  CouldNotDemoteLastAdminUserInOrganization,
  CouldNotRemoveLastAdminUserFromCourtSystem,
  CouldNotRemoveLastAdminUserFromOrganization,
  CouldNotRemoveUserGroup,
  CourtroomLimitExceeded,
  CourtSystemConfiguredWithTestPaymentKey,
  DuplicateCourtSystemNameError,
  DuplicateDepartmentName,
  DuplicateLocationName,
  FailedRecaptchaError,
  FailedToDeleteSttError,
  FailedToRequestSttError,
  FilesToRemoveAreNotFromTheSameRecording,
  ForbiddenApiError,
  InsufficientRealTimeCourtroomCapacityError,
  InvalidRecoveryCodeError,
  MaximumNumberOfTurnaroundsReachedError,
  MinimumNumberOfTurnaroundsReachedError,
  NonmatchingObjectChecksum,
  NoRemovalItemsFound,
  NotFoundApiError,
  OrderPlacementValidationError,
  OrderResubmissionValidationError,
  OrderTurnaroundNotFoundError,
  OrganizationInviteAlreadyAcceptedError,
  OrganizationInviteExpiredError,
  OrganizationInviteRevokedOrInvalidError,
  RateLimitApiError,
  RealTimeOrderRecordingIsNotLive,
  RecorderAlreadyEnabledForLocationError,
  RecorderAlreadyExistsError,
  RecorderDependenciesExistsError,
  RecorderIsEnabledWhenConfiguring,
  RecordingAlreadyHasSttError,
  RecordingDoesNotHaveRequestedTimecodesError,
  RecordingDoesNotHaveSttError,
  RecoveryCodesAlreadyExistError,
  RegistrationRateLimitedError,
  RegistrationValidationError,
  RequiredCostWaiverDocumentsNotAttached,
  RequiredSupportingDocumentsNotAttached,
  RthErrorNameMap,
  SttStillInProgressError,
  UnauthorizedApiError,
  UnknownApiError,
  UnsupportedTimeZoneError,
  UserCannotBeRemovedFromCourtSystemError,
  UserCannotBeRemovedFromCourtSystemsError,
  UserDistributionPermissionRevoked,
  UserGroupNameAlreadyExists,
  UserHasNoPaymentMethods,
  UserNotAllowedToDistributeTranscripts,
  UserNotFoundError,
  VideoConfigurationBlockedError,
  VideoLicensesExhaustedError,
} from './errors'
import { CannotChangeDefaultDepartmentAsRecordingSessionsExistForTodayError } from './errors/CannotChangeDefaultDepartmentAsRecordingSessionsExistForTodayError'
import { GoneApiError } from './errors/gone-api.error'
import { IllegalLocationStatus } from './errors/IllegalLocationStatus'
import { UnprocessableEntityError } from './errors/UnprocessableEntityError'

type ApiErrorStatusMap = Record<string, (jsonResponse: any) => Error>
type ApiStatusCodeMap = Record<number, (jsonResponse: any) => Error>

const apiErrorCodeMap: ApiErrorStatusMap = {
  [OrderErrorCode.PaymentFailedCardError]: (jsonResponse: any) => {
    if (typeof jsonResponse.error?.message === 'string') {
      return new PaymentFailedCardError(
        jsonResponse.error.message,
        jsonResponse.error.amount ? plainToClass(Money, jsonResponse.error.amount as {}) : undefined,
      )
    }

    return new UnknownApiError()
  },
  [OrderErrorCode.PaymentFailedError]: (jsonResponse: any) => {
    if (typeof jsonResponse.error?.message === 'string') {
      return new PaymentFailedError(
        jsonResponse.error.message,
        jsonResponse.error.amount ? plainToClass(Money, jsonResponse.error.amount as {}) : undefined,
      )
    }

    return new UnknownApiError()
  },
  [UserErrorCode.ValidationFailed]: () => new RegistrationValidationError(),
  [UserErrorCode.FailedRecaptcha]: () => new FailedRecaptchaError(),
  [UserErrorCode.UpstreamLimitExceeded]: () => new RegistrationRateLimitedError(),
  [UserErrorCode.UserCannotBeRemovedFromCourtSystems]: (response: any) =>
    new UserCannotBeRemovedFromCourtSystemsError(response.error),
  [OrderErrorCode.InvalidOrderPlacement]: () => new OrderPlacementValidationError(),
  [OrderErrorCode.InvalidOrderResubmission]: () => new OrderResubmissionValidationError(),
  [OrderErrorCode.OrderTurnaroundNotFound]: () => new OrderTurnaroundNotFoundError(),
  [OrderErrorCode.CannotUpdateClaimOnStaleOrder]: () => new CannotUpdateClaimError(),
  [OrderErrorCode.UserNotAllowedToDistributeTranscripts]: () => new UserNotAllowedToDistributeTranscripts(),
  [OrderErrorCode.UserDistributionPermissionRevoked]: (response: any) =>
    new UserDistributionPermissionRevoked(response.error.message),
  [OrderErrorCode.RequiredSupportingDocumentsNotAttached]: (response: any) =>
    new RequiredSupportingDocumentsNotAttached(response.error.message),
  [OrderErrorCode.CostWaiverDisabled]: (response: any) => new CostWaiverDisabled(response.error.message),
  [OrderErrorCode.RequiredCostWaiverDocumentsNotAttached]: (response: any) =>
    new RequiredCostWaiverDocumentsNotAttached(response.error.message),
  [OrderErrorCode.MissingActiveRecordingForRealTimeOrder]: () => new RealTimeOrderRecordingIsNotLive(),
  [OrderErrorCode.UserHasNoPaymentMethods]: () => new UserHasNoPaymentMethods(),
  [TranscriptTurnaroundErrorCode.MinimumNumberOfTurnaroundsReached]: () => new MinimumNumberOfTurnaroundsReachedError(),
  [TranscriptTurnaroundErrorCode.MaximumNumberOfTurnaroundsReached]: () => new MaximumNumberOfTurnaroundsReachedError(),
  [FileErrorCode.NonmatchingObjectChecksum]: () => new NonmatchingObjectChecksum(),
  [FileErrorCode.FilesToRemoveAreNotFromSameRecordingError]: () => new FilesToRemoveAreNotFromTheSameRecording(),
  [FileErrorCode.NoRemovalItemsFound]: () => new NoRemovalItemsFound(),
  [CourtSystemErrorCode.UserNotFound]: () => new UserNotFoundError(),
  [CourtSystemErrorCode.NameAlreadyExists]: () => new DuplicateCourtSystemNameError(),
  [CourtSystemErrorCode.UserCannotBeRemovedFromCourtSystem]: (response: any) =>
    new UserCannotBeRemovedFromCourtSystemError(response.error.message),
  [RecorderErrorCode.RecorderAlreadyExists]: (response: any) => new RecorderAlreadyExistsError(response.error),
  [CourtSystemErrorCode.CourtSystemConfiguredWithTestPaymentKey]: () => new CourtSystemConfiguredWithTestPaymentKey(),
  [CourtSystemErrorCode.AddonLimitBelowActiveCount]: (response: any) =>
    new AddonLimitBelowActiveCountError(response.error.message),
  [CourtSystemErrorCode.AddonLimitExceedsCourtroomLimit]: (response: any) =>
    new AddonLimitExceedsCourtroomLimitError(response.error.message),
  [LocationErrorCode.DuplicateLocationName]: () => new DuplicateLocationName(),
  [LocationErrorCode.CourtroomLimitExceeded]: () => new CourtroomLimitExceeded(),
  [LocationErrorCode.IllegalLocationStatus]: () => new IllegalLocationStatus(),
  [LocationErrorCode.VideoLicensesExhausted]: (response: any) =>
    new VideoLicensesExhaustedError(typeof response.error?.message === 'string' ? response.error.message : undefined),
  [LocationErrorCode.VideoConfigurationBlocked]: (response: any) =>
    new VideoConfigurationBlockedError(
      typeof response.error?.message === 'string' ? response.error.message : undefined,
    ),

  // The error from billing groups and user groups is a UserGroup error
  [UserGroupErrorCode.UserGroupNameAlreadyExist]: () => new UserGroupNameAlreadyExists(),
  [UserGroupErrorCode.CouldNotRemoveUserGroup]: () => new CouldNotRemoveUserGroup(),
  [UserGroupErrorCode.CouldNotRemoveLastAdminUserFromCourtSystem]: (response: any) =>
    new CouldNotRemoveLastAdminUserFromCourtSystem(response.error.message),
  [OrganizationErrorCode.CouldNotDemoteLastAdmin]: (response: any) =>
    new CouldNotDemoteLastAdminUserInOrganization(response.error.message),
  [OrganizationErrorCode.CouldNotRemoveLastAdmin]: (response: any) =>
    new CouldNotRemoveLastAdminUserFromOrganization(response.error.message),
  [OrganizationErrorCode.InviteAlreadyAccepted]: (response: any) =>
    new OrganizationInviteAlreadyAcceptedError(response.error.message),
  [OrganizationErrorCode.UserInviteExpired]: (response: any) =>
    new OrganizationInviteExpiredError(response.error.message),
  [OrganizationErrorCode.UserInviteRevokedOrInvalid]: (response: any) =>
    new OrganizationInviteRevokedOrInvalidError(response.error.message),
  [DepartmentErrorCode.DuplicateDepartmentName]: () => new DuplicateDepartmentName(),
  [RecorderErrorCode.DependenciesExist]: (response: any) => new RecorderDependenciesExistsError(response.error),
  [CourtSystemErrorCode.UnsupportedTimeZone]: (response: any) => new UnsupportedTimeZoneError(response.error.message),
  [SttErrorCode.FailedToCreateInitialSttState]: () => new FailedToRequestSttError(),
  [SttErrorCode.FailedToDeleteStt]: () => new FailedToDeleteSttError(),
  [SttErrorCode.RecordingDoesNotHaveStt]: () => new RecordingDoesNotHaveSttError(),
  [SttErrorCode.RecordingAlreadyHasStt]: () => new RecordingAlreadyHasSttError(),
  [SttErrorCode.SttStillInProgressForRecording]: () => new SttStillInProgressError(),
  [RealTimeSttErrorCode.InsufficientCourtroomCapacity]: () => new InsufficientRealTimeCourtroomCapacityError(),
  [LiveStreamErrorCode.HybridProducerNotReady]: () => new HybridProducerNotReadyError(),
  [LiveStreamErrorCode.NoHybridCapableProducerFoundForLocation]: () =>
    new NoHybridCapableProducerFoundForLocationError(),
  [SharedRecordingErrorCode.RecordingDoesNotHaveRequestedTimecodes]: () =>
    new RecordingDoesNotHaveRequestedTimecodesError(),
  [MultifactorAuthenticationErrorCode.InvalidRecoveryCode]: () => new InvalidRecoveryCodeError(),
  [MultifactorAuthenticationErrorCode.RecoveryCodesAlreadyExist]: () => new RecoveryCodesAlreadyExistError(),
  [RealtimeRecorderErrorCode.RecorderAlreadyEnabledForLocation]: (response: any) =>
    new RecorderAlreadyEnabledForLocationError(response.error),
  [RealtimeRecorderErrorCode.RecorderIsEnabledWhenConfiguring]: (response: any) =>
    new RecorderIsEnabledWhenConfiguring(response.error.message),
  [StreamLocationErrorCode.CannotChangeDefaultDepartmentAsRecordingSessionsExistForToday]: (response: any) =>
    new CannotChangeDefaultDepartmentAsRecordingSessionsExistForTodayError(response.error.message),
}

const apiErrorNameMap: ApiErrorStatusMap = {
  ...RthErrorNameMap,
}

const apiErrorStatusCodeMap: ApiStatusCodeMap = {
  [StatusCodes.NOT_FOUND]: () => new NotFoundApiError(),
  [StatusCodes.FORBIDDEN]: (response: any) => {
    const errorMessage = response?.error?.message
    return new ForbiddenApiError(typeof errorMessage === 'string' ? errorMessage : undefined)
  },
  [StatusCodes.UNAUTHORIZED]: () => new UnauthorizedApiError(),
  [StatusCodes.CONFLICT]: (error: Error) => new ConflictApiError(error),
  [StatusCodes.TOO_MANY_REQUESTS]: () => new RateLimitApiError(),
  [StatusCodes.GONE]: () => new GoneApiError(),
  [StatusCodes.UNPROCESSABLE_ENTITY]: error => new UnprocessableEntityError(error),
}

function extractMappedErrorDetails(body: unknown): { code: string; payload: Record<string, unknown> } | undefined {
  if (!body || typeof body !== 'object') {
    return undefined
  }
  const record = body as Record<string, unknown>
  const payload = record.error && typeof record.error === 'object' ? (record.error as Record<string, unknown>) : record
  const code = payload.code
  if (typeof code !== 'string') {
    return undefined
  }
  return { code, payload }
}

function mapKnownApiError(body: unknown, status: number | undefined): Error | undefined {
  const mappedErrorDetails = extractMappedErrorDetails(body)
  if (mappedErrorDetails && apiErrorCodeMap[mappedErrorDetails.code]) {
    return apiErrorCodeMap[mappedErrorDetails.code]({ error: mappedErrorDetails.payload })
  }
  const jsonResponse = body as any
  if (jsonResponse?.error && apiErrorCodeMap[jsonResponse.error.code]) {
    return apiErrorCodeMap[jsonResponse.error.code](jsonResponse)
  }
  if (jsonResponse?.error && apiErrorNameMap[jsonResponse.error]) {
    return apiErrorNameMap[jsonResponse.error](jsonResponse)
  }
  if (status && apiErrorStatusCodeMap[status]) {
    return apiErrorStatusCodeMap[status](jsonResponse)
  }
  return undefined
}

function createUnknownApiErrorFromResponse(response: HttpErrorResponse, body: unknown): UnknownApiError {
  const mappedErrorDetails = extractMappedErrorDetails(body)
  const jsonResponse = body as any

  return new UnknownApiError({
    status: response.status,
    url: response.url,
    code:
      mappedErrorDetails?.code ?? (typeof jsonResponse?.error?.code === 'string' ? jsonResponse.error.code : undefined),
    serverMessage:
      (typeof mappedErrorDetails?.payload.message === 'string' ? mappedErrorDetails.payload.message : undefined) ??
      (typeof jsonResponse?.error?.message === 'string' ? jsonResponse.error.message : undefined) ??
      (typeof jsonResponse?.message === 'string' ? jsonResponse.message : undefined),
  })
}

/**
 * Maps API errors to errors suitable for display to an end user.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiErrorMapper {
  /**
   * Extracts an error from the given HTTP response and maps it to an error suitable
   * for display to an end user.
   *
   * If the error cannot be extracted, or if no mapping exists for it, returns
   * the given default error.
   *
   * @param {HttpErrorResponse} response
   * @param {Error} fallbackError
   * @returns {Error}
   */
  mapErrorResponse(response: HttpErrorResponse, fallbackError?: Error): Error {
    if (response.error instanceof ErrorEvent) {
      return new ClientError()
    }

    const body = response.error
    return mapKnownApiError(body, response.status) ?? fallbackError ?? createUnknownApiErrorFromResponse(response, body)
  }
}
